import axios from 'axios';
import retry from 'retry';
import hardtack from 'hardtack';
import { getApiHost } from '@hotjar/common';
import type { LoginResponseDto, LoginResponse } from './types';
import { loginResponseDtoToModel } from './mapper';
import { getAccessTokenHeaders, REF_COOKIE_KEY } from '../../../utils/jwt';

export const axiosInstance = axios.create({
  baseURL: getApiHost(),
  withCredentials: true,
});

type TokenResponse = { tokens: { ACC: string } };

export const getAccessTokenWithRef = (ref: string): Promise<string | undefined> => {
  const retries = 3;
  const operation = retry.operation({
    retries,
    factor: 3,
    minTimeout: 500,
    maxTimeout: 1500,
    randomize: true,
  });
  return new Promise<string | undefined>((resolve, reject) => {
    operation.attempt((currentAttempt) => {
      axiosInstance
        .post<TokenResponse>(
          '/api/castleguard/token',
          {
            grant_type: 'refresh_token',
          },
          { headers: { 'X-Refresh-Token-Payload': ref } },
        )
        .then((response) => resolve(response?.data?.tokens?.ACC))
        .catch((error) => {
          if (currentAttempt >= retries) {
            operation.stop();
            reject();
            return;
          }

          if (error?.response?.status === 503) {
            operation.retry(error);
            return;
          }

          operation.stop();
          reject();
        });
    });
  });
};

export const loginWithEmailAndPassword = (
  email: string,
  password: string,
  remember: boolean,
): Promise<LoginResponse> => {
  return axios
    .post<LoginResponseDto>('/api/v2/users', {
      action: 'login',
      email,
      password,
      remember,
    })
    .then((response) => {
      const { data } = response;
      return loginResponseDtoToModel(data);
    });
};

export const logout = async (): Promise<void> => {
  const ref = hardtack.get(REF_COOKIE_KEY);

  if (ref) {
    const accessTokenHeaders = getAccessTokenHeaders() ?? {};

    return Promise.allSettled([
      axiosInstance.get('/engage/accounts/logout/'),
      axiosInstance.post(
        '/api/castleguard/logout',
        {},
        { headers: { 'X-Refresh-Token-Payload': ref, ...accessTokenHeaders } },
      ),
    ]).then(() => Promise.resolve());
  }

  return Promise.resolve();
};
